import classNames from "classnames";
import React, { useEffect, useState } from "react";
import HandleClicksOutside from "../HandleClicksOutside";
import Label from "./Label";
import "./ListFilter.css";
import { Flex } from "./Flex";
import Icon from "./Icon";

function flattenListItems<T extends { children?: T[] }>(items: T[]) {
    return items.reduce<T[]>(
        (prev, curr) => [...prev, curr, ...(curr.children || [])],
        [],
    );
}
export type ListFilterItem<T> = {
    data: T;
    selected: boolean;
    count?: number;
    children?: ListFilterItem<T>[];
    selectIndividually?: boolean;
};

type ListFilterProps<T> = {
    title?: string;
    items: ListFilterItem<T>[];
    onChange: (selected: T[]) => void;
    position: "bottom-right" | "bottom-left";
};

function ListFilter<T extends { id: string; name: string }>(props: ListFilterProps<T>) {
    const { items, onChange, title, position } = props;

    const [open, setOpen] = useState(false);
    const [filterState, setFilterState] = useState<{
        items: ListFilterItem<T>[];
        enabled: boolean;
    }>({
        items,
        enabled: !!flattenListItems<ListFilterItem<T>>(items).find((i) => i.selected),
    });

    useEffect(() => {
        setFilterState({
            items,
            enabled: !!flattenListItems<ListFilterItem<T>>(items).find(
                (i) => i.selected,
            ),
        });
    }, [items]);

    function toggleParentFilter(parentId: string) {
        const newList = filterState.items.map((item) => {
            if (parentId !== item.data.id) return item;
            return {
                ...item,
                selected: !item.selected,
                children:
                    item.children && !item.selectIndividually
                        ? item.children.map((it) => ({
                              ...it,
                              selected: !item.selected,
                          }))
                        : undefined,
            };
        });
        const flatNewList = flattenListItems<ListFilterItem<T>>(newList);
        const newEnabled = !!flatNewList.find((i) => i.selected);

        setFilterState({ items: newList, enabled: newEnabled });
        onChange(flatNewList.filter((it) => it.selected).map((it) => it.data));
    }

    function toggleSubFilter(newSelected: boolean, itemId: string, parentId: string) {
        const newList = filterState.items.map((item) => {
            if (parentId !== item.data.id) return item;
            return {
                ...item,
                selected: !newSelected ? false : item.selected,
                children: item.children
                    ? item.children.map((subItem) => {
                          if (itemId !== subItem.data.id) return subItem;
                          return { ...subItem, selected: newSelected };
                      })
                    : undefined,
            };
        });

        const flatNewList = flattenListItems<ListFilterItem<T>>(newList);
        const newEnabled = !!flatNewList.find((i) => i.selected);

        setFilterState({ items: newList, enabled: newEnabled });
        onChange(flatNewList.filter((it) => it.selected).map((it) => it.data));
    }

    return (
        <HandleClicksOutside
            className={classNames({
                "list-filter": true,
                "list-filter--open": open,
                "list-filter__bottom-right": position === "bottom-right",
                "list-filter__bottom-left": position === "bottom-left",
            })}
            onClickOutside={() => {
                setOpen(false);
            }}
            disabled={!open}
        >
            {" "}
            <button
                type="button"
                className="list-filter__title"
                onClick={() => {
                    setOpen(!open);
                }}
            >
                <Flex.Row gap="small">
                    {title && <Label.Medium.Normal>{title}</Label.Medium.Normal>}
                    {filterState.enabled ? (
                        <Icon.FilterList.Medium.P50 />
                    ) : (
                        <Icon.FilterList.Medium.N60 />
                    )}
                </Flex.Row>
            </button>
            {open && (
                <div className="list-filter__list">
                    {filterState.items.map(
                        (filter) =>
                            filter.count !== 0 && (
                                <React.Fragment key={filter.data.id}>
                                    <button
                                        type="button"
                                        className="list-filter__list__item"
                                        onClick={() => {
                                            toggleParentFilter(filter.data.id);
                                        }}
                                    >
                                        <div>
                                            <input
                                                type="checkbox"
                                                checked={filter.selected}
                                                readOnly
                                            />
                                            <Label.Medium>
                                                {filter.data.name}
                                            </Label.Medium>
                                        </div>
                                        {filter.count !== undefined && (
                                            <span className="count">
                                                {filter.count}
                                            </span>
                                        )}
                                    </button>
                                    {filter.children?.map((subFilter) => (
                                        <button
                                            type="button"
                                            key={subFilter.data.id}
                                            className="indent list-filter__list__item"
                                            onClick={() => {
                                                toggleSubFilter(
                                                    !subFilter.selected,
                                                    subFilter.data.id,
                                                    filter.data.id,
                                                );
                                            }}
                                        >
                                            <div>
                                                <input
                                                    type="checkbox"
                                                    checked={subFilter.selected}
                                                    readOnly
                                                />
                                                <Label.Medium>
                                                    {subFilter.data.name}
                                                </Label.Medium>
                                            </div>
                                            <span className="count">
                                                {subFilter.count}
                                            </span>
                                        </button>
                                    ))}
                                </React.Fragment>
                            ),
                    )}
                </div>
            )}
        </HandleClicksOutside>
    );
}

export default ListFilter;
